<script setup lang="ts">
import { Check, UsersRound, X } from '@lucide/vue'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PosterArt from '@/components/media/PosterArt.vue'
import UserAvatar from '@/components/ui/UserAvatar.vue'
import { useToast } from '@/composables/useToast'
import { useWatchTogetherStore } from '@/stores/watchTogether'

const watchTogether = useWatchTogetherStore()
const route = useRoute()
const router = useRouter()
const toast = useToast()
const busy = ref<'accept' | 'reject' | ''>('')
const invitation = computed(() => {
    const current = watchTogether.invitations[0]
    return current ? { ...current, host: watchTogether.resolveUser(current.host) } : undefined
})

watch(() => watchTogether.notice, (message) => {
    if (!message) { return }
    toast.show(message, 'info')
    watchTogether.clearNotice()
})

async function accept() {
    const current = invitation.value
    if (!current || busy.value) { return }
    busy.value = 'accept'
    try {
        const session = await watchTogether.acceptInvite(current)
        await router.push({
            name: 'play',
            params: { source: session.media.sourceId, id: session.media.id, episode: session.playback.episodeIndex },
            query: { watch: session.id, from: route.fullPath },
        })
    }
    catch (error) { toast.show(error instanceof Error ? error.message : '无法加入联动观影', 'error') }
    finally { busy.value = '' }
}

async function reject() {
    const current = invitation.value
    if (!current || busy.value) { return }
    busy.value = 'reject'
    try { await watchTogether.rejectInvite(current) }
    catch (error) { toast.show(error instanceof Error ? error.message : '无法处理这条邀请', 'error') }
    finally { busy.value = '' }
}
</script>

<template>
    <Transition name="watch-invite">
        <aside v-if="invitation" class="watch-invite fixed inset-x-3 bottom-[max(5.5rem,env(safe-area-inset-bottom))] z-45 mx-auto max-w-md overflow-hidden border border-[var(--c-border)] bg-[var(--c-surface-raised)] shadow-2xl md:inset-x-auto md:bottom-6 md:right-6 md:mx-0 md:w-[25rem]">
            <div class="grid grid-cols-[3.25rem_minmax(0,1fr)_auto] items-center gap-3 p-3.5">
                <div class="relative size-13 overflow-hidden rounded-md bg-[var(--c-poster)]">
                    <PosterArt :src="invitation.media.poster" :alt="invitation.media.title" :shade="false" />
                    <UserAvatar :name="invitation.host.displayName" :src="invitation.host.avatar" class="absolute size-6 ring-2 ring-[var(--c-surface-raised)] -bottom-0.5 -right-0.5" />
                </div>
                <div class="min-w-0">
                    <p class="truncate text-sm font-700">
                        {{ invitation.host.displayName }} 邀请你联动观影
                    </p>
                    <p class="mt-1 truncate text-xs text-[var(--c-text-muted)]">
                        {{ invitation.media.title }} · {{ invitation.media.episodeLabel }}
                    </p>
                    <p v-if="watchTogether.invitations.length > 1" class="mt-1 flex items-center gap-1 text-[11px] text-[var(--accent)] font-650">
                        <UsersRound class="size-3" />另有 {{ watchTogether.invitations.length - 1 }} 条邀请
                    </p>
                </div>
                <div class="flex gap-1">
                    <button class="grid size-10 place-items-center rounded-full text-[var(--c-text-muted)] transition hover:bg-[var(--c-fill-hover)] focus-ring" :disabled="Boolean(busy)" aria-label="拒绝邀请" @click="reject">
                        <X class="size-4.5" />
                    </button>
                    <button class="grid size-10 place-items-center rounded-full bg-[var(--accent)] text-white transition active:scale-95 focus-ring" :disabled="Boolean(busy)" aria-label="接受邀请" @click="accept">
                        <Check class="size-4.5" />
                    </button>
                </div>
            </div>
        </aside>
    </Transition>
</template>

<style scoped>
.watch-invite {
    border-radius: 0.75rem;
}
.watch-invite-enter-active,
.watch-invite-leave-active {
    transition:
        opacity 180ms ease,
        transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}
.watch-invite-enter-from,
.watch-invite-leave-to {
    opacity: 0;
    transform: translate3d(0, 0.75rem, 0);
}
</style>
